import { useEffect, useState, useCallback } from "react";
import api, { apiError, formatDate, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = Object.entries(ROLE_LABELS);

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "technician", customer_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: u }, { data: c }] = await Promise.all([api.get("/auth/users"), api.get("/customers")]);
      setUsers(u);
      setCustomers(c);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/auth/users", { ...form, customer_id: form.customer_id || null });
      toast.success("User created");
      setDialogOpen(false);
      setForm({ name: "", email: "", password: "", role: "technician", customer_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/auth/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${ROLE_LABELS[role]}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name}? This action is logged in the audit trail.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">User Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Role-based access control for laboratory staff and customers.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-user-button">
          <Plus className="mr-2 h-4 w-4" /> New User
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-border" data-testid="users-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="hidden md:table-cell">Linked Customer</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></TableCell></TableRow>}
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  {u.id === me.id ? (
                    <Badge variant="outline" className="font-mono text-[10px] uppercase">{ROLE_LABELS[u.role]} (you)</Badge>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="h-8 w-44" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="hidden text-sm md:table-cell">
                  {customers.find((c) => c.id === u.customer_id)?.company_name || "—"}
                </TableCell>
                <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{formatDate(u.created_at)}</TableCell>
                <TableCell>
                  {u.id !== me.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader><DialogTitle className="font-display">New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Password *</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.role === "customer" && (
              <div className="space-y-1.5">
                <Label>Link to Customer Record</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="user-form-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-form-cancel-button">Cancel</Button>
            <Button onClick={create} disabled={saving || !form.name || !form.email || form.password.length < 6} data-testid="user-form-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
